import React from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

interface AccessibilityModalProps {
  isOpen: boolean
  onClose: () => void
}

const AccessibilityModal: React.FC<AccessibilityModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-slate-900">
            {t('accessibility.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="prose prose-lg max-w-none">
            {/* Commitment Section */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                {t('accessibility.commitment.title')}
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('accessibility.commitment.description')}
              </p>
              <p className="text-slate-700 leading-relaxed">
                {t('accessibility.commitment.standards')}
              </p>
            </div>

            {/* Ongoing Efforts */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                {t('accessibility.ongoing.title')}
              </h3>
              <p className="text-slate-700 leading-relaxed">
                {t('accessibility.ongoing.description')}
              </p>
            </div>

            {/* Feedback Section */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                {t('accessibility.feedback.title')}
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                {t('accessibility.feedback.description')}
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-slate-900 mb-3">
                  {t('accessibility.feedback.contactTitle')}
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-xl mr-3">📧</span>
                    <div>
                      <span className="font-medium text-slate-900">Email: </span>
                      <a 
                        href="mailto:management@eceptionist.com"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        management@eceptionist.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xl mr-3">📞</span>
                    <div>
                      <span className="font-medium text-slate-900">Phone: </span>
                      <a 
                        href="tel:713-520-6688"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        713-520-6688
                      </a>
                    </div>
                  </div>
                </div>
                <p className="text-slate-700 mt-3 text-sm">
                  {t('accessibility.feedback.commitment')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccessibilityModal
